import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchAIResult } from '../lib/api';
import { format } from 'date-fns';

export default function AIResultDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (id) {
      fetchAIResult(Number(id))
        .then(setData)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;
  if (!data) return <p className="text-gray-500 dark:text-gray-400">{t('ai.notFound')}</p>;

  let parsedResponse: any = null;
  try {
    parsedResponse = JSON.parse(data.response);
  } catch {
    // plain text response
  }

  return (
    <div className="space-y-6">
      <Link to="/ai-results" className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
        <ArrowLeft className="w-4 h-4" /> {t('ai.backToResults')}
      </Link>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('ai.title')} #{data.id}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
          <div><span className="text-gray-500 dark:text-gray-400">{t('ai.type')}</span><p className="font-medium text-gray-800 dark:text-gray-200">{data.type}</p></div>
          <div><span className="text-gray-500 dark:text-gray-400">{t('analysis.tenant')}</span><p className="font-medium text-gray-800 dark:text-gray-200">{data.tenant_id}</p></div>
          <div><span className="text-gray-500 dark:text-gray-400">{t('ai.provider')}</span><p className="font-medium text-gray-800 dark:text-gray-200">{data.provider}</p></div>
          <div><span className="text-gray-500 dark:text-gray-400">{t('ai.cost')}</span><p className="font-medium text-gray-800 dark:text-gray-200">${data.cost_cad?.toFixed(4)}</p></div>
          <div><span className="text-gray-500 dark:text-gray-400">{t('ai.inputTokens')}</span><p className="font-medium text-gray-800 dark:text-gray-200">{data.input_tokens}</p></div>
          <div><span className="text-gray-500 dark:text-gray-400">{t('ai.outputTokens')}</span><p className="font-medium text-gray-800 dark:text-gray-200">{data.output_tokens}</p></div>
          <div><span className="text-gray-500 dark:text-gray-400">{t('ai.latency')}</span><p className="font-medium text-gray-800 dark:text-gray-200">{(data.latency_ms / 1000).toFixed(1)}s</p></div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('ai.date')}</span>
            <p className="font-medium text-gray-800 dark:text-gray-200">{data.created_at ? format(new Date(data.created_at), 'PPpp') : '-'}</p>
          </div>
        </div>
        {data.secutools_job_id && (
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">{t('ai.jobId')}: {data.secutools_job_id}</p>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{t('ai.response')}</h3>
        </div>
        <pre className="p-5 text-sm text-gray-700 dark:text-gray-300 overflow-auto max-h-[600px] whitespace-pre-wrap bg-white dark:bg-gray-900/50">
          {parsedResponse
            ? JSON.stringify(parsedResponse, null, 2)
            : data.response}
        </pre>
      </div>
    </div>
  );
}
